import { FunctionalComponent, h } from "preact";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "preact/hooks";

import { REGION_SHAPES } from "./regionShapes";
import { parseShapes } from "./parseShapes";
import style from "./style.scss";

import { COLOR_BLACK } from "utils/color";
import { routes } from "utils/urls";

type NavLink = "exhibit" | "artists" | "gameMap" | "essays";

const FILL_COLOR = "#fff";
const STROKE_COLOR = COLOR_BLACK;

const HomeNavGraphic: FunctionalComponent = () => {
  const canvasContainerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [animationsActive, setAnimationsActive] = useState(true);
  const [hoveredLink, setHoveredLink] = useState<NavLink | null>(null);
  const [canvasSize, setCanvasSize] = useState(400);
  const [navRotation, setNavRotation] = useState(0);
  const navRotationVel = -0.05;
  const globeSize = canvasSize * 0.75;
  const globeOptions = {
    afterYRotation: -2,
    baseXRotation: -0.41,
    center: canvasSize / 2,
    depth: globeSize,
    focalLength: globeSize,
    globeRadius: globeSize / 2,
    rotationYVel: 0.005,
  };
  const cancelGlobeAnimation = useRef<number>(0);
  const cancelNavAnimation = useRef<number>(0);

  const lines = useMemo(() => {
    return parseShapes(REGION_SHAPES, globeOptions.globeRadius);
  }, [globeOptions.globeRadius]);

  const rotation = {
    y: {
      cos: Math.cos(globeOptions.rotationYVel),
      sin: Math.sin(globeOptions.rotationYVel),
    },
    z: {
      cos: Math.cos(globeOptions.baseXRotation),
      sin: Math.sin(globeOptions.baseXRotation),
    },
    ay: {
      cos: Math.cos(globeOptions.afterYRotation),
      sin: Math.sin(globeOptions.afterYRotation),
    },
  };

  const animateGlobe = useCallback(updateGlobe, [
    canvasSize,
    lines,
    globeOptions.center,
    globeOptions.depth,
    globeOptions.focalLength,
    rotation.ay.cos,
    rotation.ay.sin,
    rotation.y.cos,
    rotation.y.sin,
    rotation.z.cos,
    rotation.z.sin,
    updateGlobe,
  ]);

  const animateNav = useCallback(updateNavRotation, [
    navRotation,
    updateNavRotation,
  ]);

  useEffect(() => {
    const updateCanvasSize = () => {
      if (!canvasContainerRef.current) return;
      setCanvasSize(canvasContainerRef.current.clientWidth);
    };

    window.addEventListener("resize", updateCanvasSize);

    if (canvasContainerRef.current) {
      setCanvasSize(canvasContainerRef.current.clientWidth);
    }

    return () => {
      window.removeEventListener("resize", updateCanvasSize);
      window.cancelAnimationFrame(cancelGlobeAnimation.current);
      window.cancelAnimationFrame(cancelNavAnimation.current);
    };
  }, [cancelGlobeAnimation, cancelNavAnimation]);

  if (animationsActive) {
    cancelGlobeAnimation.current = window.requestAnimationFrame(animateGlobe);
    cancelNavAnimation.current = window.requestAnimationFrame(animateNav);
  }

  return (
    <div class={style.root}>
      <div class={style.contentContainer}>
        <div ref={canvasContainerRef} class={style.canvasContainer}>
          <canvas height={canvasSize} width={canvasSize} ref={canvasRef} />
        </div>
        <div
          class={style.rotatorNav}
          style={{
            transform: `rotate(${navRotation}deg)`,
          }}
        >
          <div class={style.rotatorNavLinkRotator}>
            <a
              class={style.rotatorNavLink}
              href={routes.exhibit.toLink()}
              onPointerEnter={() => onNavItemPointerEnter("exhibit")}
              onPointerLeave={onNavItemPointerLeave}
            >
              View exhibit
            </a>
          </div>
          <div class={style.rotatorNavLinkRotator}>
            <a
              class={style.rotatorNavLink}
              href={routes.search.toLink({
                view: "list",
              })}
              onPointerEnter={() => onNavItemPointerEnter("artists")}
              onPointerLeave={onNavItemPointerLeave}
            >
              Search artists
            </a>
          </div>
          <div class={style.rotatorNavLinkRotator}>
            <a
              class={style.rotatorNavLink}
              href={routes.search.toLink({
                view: "game-map",
              })}
              onPointerEnter={() => onNavItemPointerEnter("gameMap")}
              onPointerLeave={onNavItemPointerLeave}
            >
              View game map
            </a>
          </div>
          <div class={style.rotatorNavLinkRotator}>
            <a
              class={style.rotatorNavLink}
              href={`${routes.about.toLink()}#essays`}
              onPointerEnter={() => onNavItemPointerEnter("essays")}
              onPointerLeave={onNavItemPointerLeave}
            >
              Read essays
            </a>
          </div>
        </div>
        <div class={style.banner}>
          <div class={style.bannerTitle}>
            <h1 class={style.bannerTitleMain}>TELEPHONE</h1>
            <div class={style.bannerTitleSub}>
              A game of art whispered around the world
            </div>
          </div>
          <div
            class={style.bannerLinkInfo}
            data-visible={String(hoveredLink === "artists")}
          >
            Search the list of artists who participated
          </div>
          <div
            class={style.bannerLinkInfo}
            data-visible={String(hoveredLink === "essays")}
          >
            Read essays written about the project
          </div>
          <div
            class={style.bannerLinkInfo}
            data-visible={String(hoveredLink === "exhibit")}
          >
            View the exhibit and surf through artists
          </div>
          <div
            class={style.bannerLinkInfo}
            data-visible={String(hoveredLink === "gameMap")}
          >
            View the map of how the art is connected
          </div>
        </div>
        <div class={style.mobileNav}>
          <a
            class={style.mobileNavItem}
            href={routes.search.toLink({
              view: "list",
            })}
          >
            Search artists
          </a>
          <a
            class={style.mobileNavItem}
            href={routes.search.toLink({
              view: "game-map",
            })}
          >
            View game map
          </a>
          <a class={style.mobileNavItem} href={routes.exhibit.toLink()}>
            View exhibit
          </a>
          <a class={style.mobileNavItem} href={routes.about.toLink()}>
            About the project
          </a>
        </div>
      </div>
    </div>
  );

  function updateGlobe() {
    const ctx = canvasRef.current?.getContext("2d");

    if (!ctx) return;

    ctx.fillStyle = FILL_COLOR;
    ctx.fillRect(0, 0, canvasSize, canvasSize);
    ctx.strokeStyle = STROKE_COLOR;
    ctx.beginPath();

    for (let i = 0; i < lines.length; ++i) {
      const points = lines[i];

      for (let j = 0; j < points.length; ++j) {
        const point = points[j];
        let x = point.x;
        let y = point.y;
        let z = point.z;

        let X = x;
        x = x * rotation.y.cos - z * rotation.y.sin;
        z = z * rotation.y.cos + X * rotation.y.sin;

        point.x = x;
        point.z = z;

        const Y = y;
        y = y * rotation.z.cos - x * rotation.z.sin;
        x = x * rotation.z.cos + Y * rotation.z.sin;

        X = x;
        x = x * rotation.ay.cos - z * rotation.ay.sin;
        z = z * rotation.ay.cos + X * rotation.ay.sin;

        z += globeOptions.depth;

        const scale = globeOptions.focalLength / z;
        const sx = globeOptions.center + scale * x;
        const sy = globeOptions.center + scale * y;

        point.sx = sx;
        point.sy = sy;

        ctx[j === 0 ? "moveTo" : "lineTo"](sx, sy);
      }

      ctx.lineTo(points[0].sx, points[0].sy);
    }

    ctx.stroke();
  }

  function onNavItemPointerEnter(link: NavLink) {
    cancelAnimationFrame(cancelGlobeAnimation.current);
    cancelAnimationFrame(cancelNavAnimation.current);
    setAnimationsActive(false);
    setHoveredLink(link);
  }

  function onNavItemPointerLeave() {
    setAnimationsActive(true);
    setHoveredLink(null);
    animateGlobe();
    animateNav();
  }

  function updateNavRotation() {
    let next = navRotation + navRotationVel;
    if (next <= -360) {
      next = 0;
    }
    setNavRotation(next);
  }
};

export default HomeNavGraphic;
